import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  IconButton,
  Chip,
  Skeleton,
} from '@mui/material';
import React, { useState } from 'react';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { SortPopover } from 'components/shared/SortPopOver';
import zeplinColor from 'theme/Color';
import { Lead, LeadKanbanColumnId } from 'model/leads.model';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import { ReactComponent as VerticalDots } from 'assets/vertical-dots.svg';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import { ReactComponent as ViewIcon } from 'assets/view.svg';
import { ReactComponent as CopyIcon } from 'assets/copy-link.svg';
import { ReactComponent as ConvertClientIcon } from 'assets/convert-client.svg';
import { ReactComponent as ChangeStatusIcon } from 'assets/change-status.svg';
import { ReactComponent as ArrowIcon } from 'assets/arrow-next.svg';
import DeleteIcon from 'assets/delete-red.png';
import EditIcon from 'assets/edit.svg';
import CustomDeleteModal from 'components/shared/CustomDeleteModal/CustomDeleteModal';
import { modalReducer } from 'reducers/modalReducer';
import Pagination from 'components/shared/CustomPagination/CustomPagination';
import { setEditLeadData } from 'store/slices/leadsSlice';
import { useAppDispatch } from 'store/hooks';

// Global status color mapping
const STATUS_COLOR_MAP: Record<LeadKanbanColumnId, string> = {
  open: zeplinColor.Neutral8,
  contacted: zeplinColor.AlertInfo,
  'no-response': zeplinColor.Blue,
  responded: zeplinColor.Success,
  qualified: zeplinColor.Success,
  unqualified: zeplinColor.Neutral4,
};

type LeadsKanbanTableProps = {
  leads: Lead[];
  onStatusChange?: (leadId: string, status: LeadKanbanColumnId) => void;
  onDelete?: (leadId: string, onComplete?: () => void) => void;
  onViewDetails?: (lead: Lead) => void;
  onSort?: (field: string, order: 'asc' | 'desc') => void;
  sortBy?: string[];
  sortOrder?: ('asc' | 'desc')[];
  rowsPerPage?: number;
  setRowsPerPage?: (value: number) => void;
  currentPage?: number;
  setCurrentPage?: (value: number) => void;
  totalCount?: number;
  isLoading?: boolean;
  isDeleting?: boolean;
};

const statusLabels: Record<LeadKanbanColumnId, string> = {
  open: 'Open',
  contacted: 'Contacted',
  'no-response': 'No Response',
  responded: 'Responded',
  qualified: 'Qualified',
  unqualified: 'Unqualified',
};

const statusOptions: LeadKanbanColumnId[] = [
  'open',
  'contacted',
  'no-response',
  'responded',
  'qualified',
  'unqualified',
];

export const LeadsKanbanTable = ({
  leads,
  onStatusChange,
  onDelete,
  onViewDetails,
  onSort,
  sortBy = [],
  sortOrder = [],
  rowsPerPage = 10,
  setRowsPerPage,
  currentPage = 0,
  setCurrentPage,
  totalCount = 0,
  isLoading = false,
  isDeleting,
}: LeadsKanbanTableProps) => {
  const dispatch = useAppDispatch();
  const [anchorEl, setAnchorEl] = useState<SVGSVGElement | null>(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLDivElement | null>(null);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [modalState, modalDispatch] = React.useReducer(modalReducer, {});
  const [nameSortAnchorEl, setNameSortAnchorEl] = useState<null | HTMLElement>(null);
  const [statusSortAnchorEl, setStatusSortAnchorEl] = useState<null | HTMLElement>(null);
  const [agencySortAnchorEl, setAgencySortAnchorEl] = useState<null | HTMLElement>(null);
  const [assigneeSortAnchorEl, setAssigneeSortAnchorEl] = useState<null | HTMLElement>(null);
  const [lastModifiedSortAnchorEl, setLastModifiedSortAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [nameSortOption, setNameSortOption] = useState<'asc' | 'desc'>('asc');
  const [statusSortOption, setStatusSortOption] = useState<'asc' | 'desc'>('asc');
  const [agencySortOption, setAgencySortOption] = useState<'asc' | 'desc'>('asc');
  const [assigneeSortOption, setAssigneeSortOption] = useState<'asc' | 'desc'>('asc');
  const [lastModifiedSortOption, setLastModifiedSortOption] = useState<'asc' | 'desc'>('desc');
  const [pageWindowStart, setPageWindowStart] = useState(0);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const handleFirstPage = () => {
    if (setCurrentPage) {
      setCurrentPage(0);
      setPageWindowStart(0);
    }
  };

  const handlePreviousPage = () => {
    if (setCurrentPage && currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (setCurrentPage && currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleLastPage = () => {
    if (setCurrentPage) {
      setCurrentPage(totalPages - 1);
      const maxWindowStart = Math.max(0, totalPages - 4);
      setPageWindowStart(maxWindowStart);
    }
  };

  const handlePageChangeClick = (page: number) => {
    if (setCurrentPage) {
      setCurrentPage(page);
    }
  };

  const handleRowsPerPageChangeClick = (value: number) => {
    if (setRowsPerPage) {
      setRowsPerPage(value);
    }
    if (setCurrentPage) {
      setCurrentPage(0);
    }
  };

  const handleOptionsClick = (e: React.MouseEvent<SVGSVGElement>, leadId: string) => {
    e.stopPropagation();
    setSelectedLeadId(leadId);
    setAnchorEl(e.currentTarget);
  };

  const handleChangeStatus = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setStatusAnchorEl(e.currentTarget);
  };

  const handleSelectStatus = (status: LeadKanbanColumnId) => {
    if (selectedLeadId && onStatusChange) {
      onStatusChange(selectedLeadId, status);
    }
    setStatusAnchorEl(null);
    setAnchorEl(null);
  };

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedLeadId && onViewDetails) {
      const selectedLead = leads.find((l) => l.leadId === selectedLeadId);
      if (selectedLead) {
        onViewDetails(selectedLead);
      }
    }
    setAnchorEl(null);
  };

  const handleEditLead = () => {
    if (selectedLeadId) {
      const selectedLead = leads.find((l) => l.leadId === selectedLeadId);
      if (selectedLead) {
        dispatch(setEditLeadData(selectedLead));
      }
    }
    setAnchorEl(null);
  };

  const handleConvertToClient = (e: React.MouseEvent) => {
    e.stopPropagation();

    setAnchorEl(null);
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    const leadLink = `${window.location.origin}/leads/${selectedLeadId}`;
    navigator.clipboard.writeText(leadLink);
    setAnchorEl(null);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalDispatch({ type: 'OPEN', payload: 'delete' });
    setAnchorEl(null);
  };

  const handleConfirmDelete = () => {
    if (selectedLeadId && onDelete) {
      onDelete(selectedLeadId, () => {
        modalDispatch({ type: 'CLOSE', payload: 'delete' });
      });
    }
  };

  const handleNameSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setNameSortAnchorEl(event.currentTarget);
  };

  const handleStatusSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setStatusSortAnchorEl(event.currentTarget);
  };

  const handleAgencySortClick = (event: React.MouseEvent<HTMLElement>) => {
    setAgencySortAnchorEl(event.currentTarget);
  };

  const handleAssigneeSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setAssigneeSortAnchorEl(event.currentTarget);
  };

  const handleLastModifiedSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setLastModifiedSortAnchorEl(event.currentTarget);
  };

  const handleSortOption = (
    field: string,
    order: 'asc' | 'desc',
    setAnchor: React.Dispatch<React.SetStateAction<HTMLElement | null>>,
  ) => {
    if (onSort) {
      onSort(field, order);
    }
    setAnchor(null);
  };

  const handleNameSortChange = (value: 'asc' | 'desc') => {
    setNameSortOption(value);
    handleSortOption('name', value, setNameSortAnchorEl);
  };

  const handleStatusSortChange = (value: 'asc' | 'desc') => {
    setStatusSortOption(value);
    handleSortOption('status', value, setStatusSortAnchorEl);
  };

  const handleAgencySortChange = (value: 'asc' | 'desc') => {
    setAgencySortOption(value);
    handleSortOption('agency', value, setAgencySortAnchorEl);
  };

  const handleAssigneeSortChange = (value: 'asc' | 'desc') => {
    setAssigneeSortOption(value);
    handleSortOption('createdBy', value, setAssigneeSortAnchorEl);
  };

  const handleLastModifiedSortChange = (value: 'asc' | 'desc') => {
    setLastModifiedSortOption(value);
    handleSortOption('updatedAt', value, setLastModifiedSortAnchorEl);
  };

  const getLeadName = (lead: Lead) => {
    return lead.name || 'N/A';
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getLeadStatus = (lead: Lead) => {
    const status = lead.status?.toLowerCase().replace(/\s+/g, '-') || 'open';
    return statusLabels[status as LeadKanbanColumnId] || lead.status || 'N/A';
  };

  const getStatusColor = (status: string): string => {
    const statusKey = status.toLowerCase().replace(/\s+/g, '-') as LeadKanbanColumnId;
    return STATUS_COLOR_MAP[statusKey] || zeplinColor.Neutral8;
  };

  const formatDate = (date: string) => {
    const d = new Date(date);
    const day = d.getDate();
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const year = d.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  const open = Boolean(anchorEl);
  const statusOpen = Boolean(statusAnchorEl);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
      <TableContainer
        sx={{ borderRadius: '14px', maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}
      >
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow
              sx={{
                //   backgroundColor: zeplinColor.Neutral2,
                borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                '& th': {
                  backgroundColor: zeplinColor.Neutral3,
                  fontWeight: 600,
                  color: zeplinColor.BasicBlack,
                },
                '& th:first-of-type': {
                  borderRadius: '14px 0px 0px 14px',
                },
                '& th:last-child': {
                  borderRadius: '0px 14px 14px 0px',
                },
              }}
            >
              <TableCell
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                    Name
                  </Typography>
                  <IconButton size='small' onClick={handleNameSortClick} sx={{ padding: '2px' }}>
                    <UnfoldMoreIcon sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                    Status
                  </Typography>
                  <IconButton size='small' onClick={handleStatusSortClick} sx={{ padding: '2px' }}>
                    <UnfoldMoreIcon sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                }}
              >
                <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                  Sport Category
                </Typography>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                    Agency
                  </Typography>
                  <IconButton size='small' onClick={handleAgencySortClick} sx={{ padding: '2px' }}>
                    <UnfoldMoreIcon sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                }}
              >
                <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                  Email
                </Typography>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                    Assignee
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={handleAssigneeSortClick}
                    sx={{ padding: '2px' }}
                  >
                    <UnfoldMoreIcon sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell
                align='left'
                sx={{
                  padding: '12px 16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                    Last Modified
                  </Typography>
                  <IconButton
                    size='small'
                    onClick={handleLastModifiedSortClick}
                    sx={{ padding: '2px' }}
                  >
                    <UnfoldMoreIcon sx={{ fontSize: '16px', color: zeplinColor.BasicBlack }} />
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell
                align='center'
                sx={{
                  fontWeight: 600,
                  color: zeplinColor.BasicBlack,
                  padding: '12px 16px',
                  fontSize: '14px',
                  width: '50px',
                }}
              >
                {' '}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: rowsPerPage }).map((_, index) => (
                <TableRow
                  key={`lead-row-skeleton-${index}`}
                  sx={{ borderBottom: `1px solid ${zeplinColor.Neutral5}` }}
                >
                  <TableCell sx={{ padding: '12px 16px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Skeleton variant='circular' width={32} height={32} />
                      <Skeleton variant='text' width='70%' height={20} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ padding: '12px 16px' }}>
                    <Skeleton variant='rounded' width={80} height={24} />
                  </TableCell>
                  <TableCell sx={{ padding: '12px 16px' }}>
                    <Skeleton variant='rounded' width={90} height={24} />
                  </TableCell>
                  <TableCell sx={{ padding: '12px 16px' }}>
                    <Skeleton variant='text' width='60%' height={20} />
                  </TableCell>
                  <TableCell sx={{ padding: '12px 16px' }}>
                    <Skeleton variant='text' width='70%' height={20} />
                  </TableCell>
                  <TableCell sx={{ padding: '12px 16px' }}>
                    <Skeleton variant='text' width='60%' height={20} />
                  </TableCell>
                  <TableCell sx={{ padding: '12px 16px' }}>
                    <Skeleton variant='text' width='50%' height={20} />
                  </TableCell>
                  <TableCell align='center' sx={{ padding: '12px 16px' }}>
                    <Skeleton variant='circular' width={20} height={20} />
                  </TableCell>
                </TableRow>
              ))
            ) : !isLoading && leads.length === 0 ? (
              <TableRow sx={{ borderBottom: `1px solid ${zeplinColor.Neutral5}` }}>
                <TableCell colSpan={8} sx={{ padding: '24px 16px' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img src='/assets/leads-icon.svg' style={{ width: '88px', height: '88px' }} />
                    <Typography
                      variant='responsiveHeading3'
                      color={zeplinColor.BasicBlack}
                      sx={{ marginTop: { xs: '22px', xl: '24px' } }}
                    >
                      No Leads Yet
                    </Typography>
                    <Typography
                      variant='responsiveBody3'
                      color={zeplinColor.Neutral8}
                      sx={{
                        width: '70%',
                        textAlign: 'center',
                        marginTop: { xs: '10px', xl: '12px' },
                      }}
                    >
                      Looks like there are no leads here yet. New leads will appear once added.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              leads.map((lead, index) => (
                <TableRow
                  key={lead.leadId}
                  onClick={() => onViewDetails?.(lead)}
                  sx={{
                    borderBottom: `1px solid ${zeplinColor.Neutral5}`,
                    '&:hover': {
                      backgroundColor: zeplinColor.Neutral2,
                      cursor: 'pointer',
                    },
                  }}
                >
                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Avatar
                        src={lead.profile_image_url}
                        sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: zeplinColor.BasicBlack,
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(getLeadName(lead))}
                      </Avatar>
                      <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
                        {getLeadName(lead)}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 8px',
                        borderRadius: '50px',
                        backgroundColor: getStatusColor(getLeadStatus(lead)),
                      }}
                    >
                      <Typography
                        variant='body3'
                        sx={{ color: zeplinColor.Neutral1, fontWeight: 500 }}
                      >
                        {getLeadStatus(lead)}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Chip
                      label={
                        <Typography variant='responsiveLabel1' color={zeplinColor.Neutral1}>
                          {lead?.sport || 'N/A'}
                        </Typography>
                      }
                      sx={{
                        backgroundColor: zeplinColor.BasicBlack,
                        padding: '5px 12px 5px 12px',
                        borderRadius: '50px',
                        height: '24px',
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
                      {lead?.agencyName || 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
                      {lead?.email || 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
                      {lead?.createdBy || 'N/A'}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ padding: '12px 16px', fontSize: '14px' }}>
                    <Typography variant='body3' sx={{ color: zeplinColor.BasicBlack }}>
                      {formatDate(lead?.updatedAt || lead?.createdAt)}
                    </Typography>
                  </TableCell>

                  <TableCell align='center' sx={{ padding: '12px 16px' }}>
                    <LightTooltip
                      title={
                        <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                          Options
                        </Typography>
                      }
                      placement='bottom-end'
                      style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                    >
                      <VerticalDots
                        onClick={(e) => handleOptionsClick(e, lead.leadId)}
                        style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                      />
                    </LightTooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        setCurrentPage={setCurrentPage || (() => {})}
        setRowsPerPage={setRowsPerPage || (() => {})}
        currentPage={currentPage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        count={totalCount}
        paginatedDataLength={leads.length}
        onFirstPage={handleFirstPage}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onLastPage={handleLastPage}
        onPageChange={handlePageChangeClick}
        onRowsPerPageChange={handleRowsPerPageChangeClick}
        pageWindowStart={pageWindowStart}
        setPageWindowStart={setPageWindowStart}
      />

      <Box>
        {/* Options Popover */}
        <CustomPopOver
          onClick={(e: any) => {
            e.stopPropagation();
          }}
          setAnchorEl={setAnchorEl}
          anchorEl={anchorEl}
          id={'simple-popover'}
          sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              padding: '16px',
            }}
          >
            <Box
              sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
              onClick={handleConvertToClient}
            >
              <ConvertClientIcon width={18} height={18} />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Convert to client
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: '9px',
                cursor: 'pointer',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              onClick={handleChangeStatus}
            >
              <Box sx={{ display: 'flex', gap: '9px', alignItems: 'center' }}>
                <ChangeStatusIcon width={18} height={18} />
                <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                  Change status
                </Typography>
              </Box>
              <ArrowIcon width={16} height={16} />
            </Box>
            <Box
              sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
              onClick={handleViewDetails}
            >
              <ViewIcon width={18} height={18} />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                View details
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleEditLead}>
              <img src={EditIcon} width={18} height={18} alt='edit' />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Edit lead
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleCopyLink}>
              <CopyIcon width={18} height={18} />
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                Copy link
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }} onClick={handleDelete}>
              <img src={DeleteIcon} width={18} height={18} alt='delete' />
              <Typography variant='responsiveBody3' color={zeplinColor.Error}>
                Delete
              </Typography>
            </Box>
          </Box>
        </CustomPopOver>

        {/* Status Selection Popover */}
        <CustomPopOver
          onClick={(e: any) => {
            e.stopPropagation();
          }}
          setAnchorEl={setStatusAnchorEl}
          anchorEl={statusAnchorEl}
          id={undefined}
          sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)', right: '-1rem' }}
          horizontal='right'
          vertical='top'
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              padding: '12px 0px',
            }}
          >
            {statusOptions.map((status) => (
              <Box
                key={status}
                sx={{
                  display: 'flex',
                  gap: '12px',
                  cursor: 'pointer',
                  padding: '8px 16px',
                  alignItems: 'center',

                  '&:hover': {
                    backgroundColor: zeplinColor.Neutral5,
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectStatus(status);
                }}
              >
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: selectedLeadId
                      ? leads
                          .find((l) => l.leadId === selectedLeadId)
                          ?.status?.toLowerCase()
                          .replace(/\s+/g, '-') === status
                        ? zeplinColor.BasicBlack
                        : 'transparent'
                      : 'transparent',
                  }}
                />
                <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                  {statusLabels[status]}
                </Typography>
              </Box>
            ))}
          </Box>
        </CustomPopOver>

        {/* Delete Modal */}
        {selectedLeadId && (
          <CustomDeleteModal
            isModalOpen={modalState.delete || false}
            modalDispatch={modalDispatch}
            onDelete={handleConfirmDelete}
            title='Delete Lead?'
            itemName={leads.find((l) => l.leadId === selectedLeadId)?.name}
            description={`Are you sure you want to delete ${leads.find((l) => l.leadId === selectedLeadId)?.name}? This action cannot be undone.`}
            isDeleting={isDeleting}
          />
        )}

        {/* Sort Popovers */}
        <SortPopover
          anchorEl={nameSortAnchorEl}
          setAnchorEl={setNameSortAnchorEl}
          sortOption={nameSortOption}
          onSortChange={handleNameSortChange}
          label='Name'
          ascLabel='A to Z'
          descLabel='Z to A'
        />
        <SortPopover
          anchorEl={statusSortAnchorEl}
          setAnchorEl={setStatusSortAnchorEl}
          sortOption={statusSortOption}
          onSortChange={handleStatusSortChange}
          label='Status'
          ascLabel='A to Z'
          descLabel='Z to A'
        />
        <SortPopover
          anchorEl={agencySortAnchorEl}
          setAnchorEl={setAgencySortAnchorEl}
          sortOption={agencySortOption}
          onSortChange={handleAgencySortChange}
          label='Agency'
          ascLabel='A to Z'
          descLabel='Z to A'
        />
        <SortPopover
          anchorEl={assigneeSortAnchorEl}
          setAnchorEl={setAssigneeSortAnchorEl}
          sortOption={assigneeSortOption}
          onSortChange={handleAssigneeSortChange}
          label='Assignee'
          ascLabel='A to Z'
          descLabel='Z to A'
        />
        <SortPopover
          anchorEl={lastModifiedSortAnchorEl}
          setAnchorEl={setLastModifiedSortAnchorEl}
          sortOption={lastModifiedSortOption}
          onSortChange={handleLastModifiedSortChange}
          label='Last Modified'
          ascLabel='Oldest to Newest'
          descLabel='Newest to Oldest'
        />
      </Box>
    </Box>
  );
};
